import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_ENDPOINTS } from '../../../utils/config';
import './CompanySelector.scss';

export interface CompanySelectorProps {
  /** Selected company IDs (ObjectId array) */
  value: Array<{ company_id: string; company_name: string }>;
  /** Callback when selection changes */
  onChange: (companies: Array<{ company_id: string; company_name: string }>) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to allow multiple selection */
  multiple?: boolean;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** CSS class name */
  className?: string;
  /** Error message */
  error?: string;
  /** Whether to use a portal for the dropdown (default: true). Set to false to render inline. */
  usePortal?: boolean;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({
  value = [],
  onChange,
  placeholder = 'Select companies...',
  multiple = true,
  disabled = false,
  className = '',
  error,
  usePortal = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allCompanies, setAllCompanies] = useState<any[]>([]); // Store all companies
  const [companies, setCompanies] = useState<any[]>([]); // Filtered companies
  const [isLoading, setIsLoading] = useState(false);

  // Ref for the container to calculate position
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // State for dropdown positioning
  const [dropdownStyle, setDropdownStyle] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  // Update dropdown position
  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // Different positioning logic based on portal usage
      if (usePortal) {
        setDropdownStyle({
          top: rect.bottom + 8, // 8px gap
          left: rect.left,
          width: rect.width
        });
      } else {
        // Relative positioning when no portal (handled by CSS primarily, but can set width here)
        setDropdownStyle({
          top: 0, // Ignored by valid CSS or handled by inline styles below
          left: 0,
          width: rect.width
        });
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside container (trigger) OR inside portal dropdown
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);

      if (isOpen && !isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    // Update position on scroll/resize
    const handleScrollResize = () => {
      if (isOpen) updatePosition();
    };

    if (isOpen) {
      updatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollResize, true); // true for capture (nested scrolls)
      window.addEventListener('resize', handleScrollResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollResize, true);
      window.removeEventListener('resize', handleScrollResize);
    };
  }, [isOpen]);

  // Fetch all companies from backend when dropdown opens
  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        // Get all companies with high limit
        const params = new URLSearchParams({
          page: '1',
          limit: '10000' // Very high limit to get all companies
        });

        const response = await fetch(`${API_ENDPOINTS.COMPANIES}?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch companies');
        }

        const result = await response.json();
        if (result.success) {
          const fetchedCompanies = result.data || [];
          setAllCompanies(fetchedCompanies);
          // Initially show all companies
          setCompanies(fetchedCompanies);
        } else {
          console.error('API reported failure:', result.message);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && allCompanies.length === 0) {
      // Only fetch if we don't have companies cached
      fetchCompanies();
    }
  }, [isOpen]);

  // Filter companies client-side based on search query
  useEffect(() => {
    if (!searchQuery || !searchQuery.trim()) {
      // If no search query, show all companies
      setCompanies(allCompanies);
      return;
    }

    // Client-side filtering - search in company name (case-insensitive)
    const query = searchQuery.trim().toLowerCase();
    const filtered = allCompanies.filter((company) => {
      const companyName = (company.name || '').toLowerCase();
      return companyName.includes(query);
    });

    setCompanies(filtered);
  }, [searchQuery, allCompanies]);

  // Handle company selection
  const handleCompanySelect = (company: any) => {
    if (disabled) return;

    const companyData = {
      company_id: company._id,
      company_name: company.name
    };

    if (multiple) {
      const exists = value.some(c => c.company_id === company._id);
      const newValue = exists
        ? value.filter(c => c.company_id !== company._id)
        : [...value, companyData];
      onChange(newValue);
    } else {
      onChange([companyData]);
      setIsOpen(false);
    }
  };

  // Handle remove company
  const handleRemoveCompany = (companyId: string) => {
    if (disabled) return;
    onChange(value.filter(c => c.company_id !== companyId));
  };

  // Handle clear all
  const handleClearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const selectedCount = value.length;
  const hasSelection = selectedCount > 0;
  const labelText = hasSelection ? `${selectedCount} selected` : placeholder;

  const containerClassName = [
    'company-selector',
    className,
    error ? 'error' : '',
    disabled ? 'disabled' : '',
    isOpen ? 'open' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const labelClassName = `company-selector__label${hasSelection ? '' : ' placeholder'}`;

  // Portal Dropdown Content
  const dropdownContent = (
    <div
      className="company-selector__dropdown"
      ref={dropdownRef}
      style={
        usePortal
          ? {
            position: 'fixed',
            top: dropdownStyle.top,
            left: dropdownStyle.left,
            width: dropdownStyle.width,
            zIndex: 9999999 // High z-index for portal
          }
          : {
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            zIndex: 1000, // Standard dropdown z-index
            marginTop: '4px'
          }
      }
    >
      {/* Search Input */}
      <div className="company-selector__search">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search companies..."
          className="company-selector__search-input"
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
      </div>

      {/* Company List */}
      <div className="company-selector__list">
        {isLoading ? (
          <div className="company-selector__loading">Loading companies...</div>
        ) : companies.length === 0 ? (
          <div className="company-selector__empty">No companies found</div>
        ) : (
          companies.map((company) => {
            const isSelected = value.some(c => c.company_id === company._id);
            return (
              <div
                key={company._id}
                className={`company-selector__item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleCompanySelect(company)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => { }}
                  className="company-selector__checkbox"
                />
                <span className="company-selector__item-name">{company.name}</span>
              </div>
            );
          })
        )}
      </div>

      {/* Footer Actions */}
      {multiple && value.length > 0 && (
        <div className="company-selector__footer">
          <button
            className="company-selector__clear"
            onClick={(e) => {
              e.stopPropagation();
              handleClearAll();
            }}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={containerClassName} ref={containerRef}>
      {/* Clickable Label Trigger */}
      <div
        className="company-selector__trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={labelClassName}>{labelText}</span>
        <span className="company-selector__arrow">{isOpen ? '▲' : '▼'}</span>
      </div>

      {/* Portal Dropdown OR Inline Dropdown */}
      {isOpen && !disabled && (
        usePortal
          ? createPortal(dropdownContent, document.body)
          : dropdownContent
      )}

      {/* Error Message */}
      {error && <div className="company-selector__error">{error}</div>}
    </div>
  );
};

export default CompanySelector;
